import React from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import TeamShareCodeChip from '../components/TeamShareCodeChip';
import type { Team } from '../api/types';
import type { PublicRunState } from '../services/publicRuns';

type TeamSubTab = 'leaderboard' | 'chat' | 'info';

interface RunShellOutletContext {
  team: Team | null;
  run: PublicRunState | null;
}

const TeamTab: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { t } = useTranslation('runs');
  const location = useLocation();
  const navigate = useNavigate();
  // `run` is forwarded, not just consumed here: the chat below needs the run
  // state to know whether sending is still possible (#495).
  const { team, run } = useOutletContext<RunShellOutletContext>();

  const last = location.pathname.split('/').filter(Boolean).pop() ?? '';
  const active: TeamSubTab =
    last === 'chat' ? 'chat' : last === 'info' ? 'info' : 'leaderboard';

  const handleChange = (_: React.SyntheticEvent, next: TeamSubTab) => {
    navigate(`/runs/${runId}/participant/team/${next}`);
  };

  return (
    <Box>
      {team?.share_code && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('team.shareCode.intro')}
          </Typography>
          <TeamShareCodeChip code={team.share_code} variant="full" />
        </Stack>
      )}
      <Tabs
        value={active}
        onChange={handleChange}
        variant="fullWidth"
        sx={{
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tab value="leaderboard" label={t('participant.nav.leaderboard')} />
        <Tab value="chat" label={t('participant.nav.chat')} />
        <Tab value="info" label={t('team.info.title')} />
      </Tabs>
      <Outlet context={{ team, run }} />
    </Box>
  );
};

export default TeamTab;
